import {
  ADWORDS_GLOBAL_TAG,
  GOOGLE_ANALYTICS_4_TRACKING_ID,
} from "@shared/env";
import { enforceGaCookieCap } from "@shared/utils/cookies/cookie-cap";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { appendScriptToHead, createElement } from "@shared/utils/dom";

interface GtagConfig {
  debug_mode: boolean;
  cookie_flags: string;
  linker: {
    accept_incoming: boolean;
  };
  client_id?: string;
  session_id?: string;
  page_referrer?: string;
}

// This is a variadic function since this is using 'arguments' object directly
// Also this cannot be defined as arrow function o/w variadic doesn't work
function gtag() {
  if (window.dataLayer) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as unknown[]);
  }
}

// For loading multiple Gtags dynamically, remember we only need to load gtag script once (with any gtag id)
// And for following gtags, we can just use the window.gtag function's 'config' to initialize others
export class GTagManager {
  private activeTagIds: Set<string> = new Set();
  private initializePromise: Promise<boolean> | undefined;

  constructor() {
    const showpassEnvOptions = [
      GOOGLE_ANALYTICS_4_TRACKING_ID,
      ADWORDS_GLOBAL_TAG,
    ];
    showpassEnvOptions.forEach((env) => {
      // undefined env variables are picked up as 'undefined' string
      if (env && env.toString() !== "undefined") {
        this.loadGTag(env);
      }
    });
  }

  isGtagAvailable = () => {
    return typeof window !== "undefined" && !!window.gtag;
  };

  // Method to load the gtag.js script and initialize it with the given tag ID
  public async loadGTag(tagId: string) {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !tagId
    ) {
      return;
    }

    await this.loadGTagScript(tagId);

    // Gtag should be available to start config
    this.configGTag(tagId);
    this.activeTagIds.add(tagId);
    // eslint-disable-next-line
    // @ts-ignore
    delete window[`ga-disable-${tagId}`]; // if tag was disabled, re-enable it
  }

  // Method to check if a tag ID has already been configured in the dataLayer
  isTagConfigured = (tagId: string): boolean => {
    const availableConfigs = window?.dataLayer?.filter((x) => {
      if (x && typeof x === "object") {
        return (x as unknown[])[0] === "config";
      }
      return false;
    });
    if (!availableConfigs) return false;

    return availableConfigs?.some(
      (config) => (config as unknown[])[1] === tagId
    );
  };

  // Method to configure gtag with a new tag ID
  configGTag = (tagId: string): void => {
    // if this id is already configured, we can skip it
    if (this.isTagConfigured(tagId) || this.activeTagIds.has(tagId)) {
      return;
    }

    // Prune excess _ga_* cookies before configuring a new property,
    // which will create yet another _ga_XXXXXXXXXX cookie.
    enforceGaCookieCap();

    // If gtag is already loaded, just configure it with the new tag ID
    if (window.gtag) {
      let config: GtagConfig = {
        debug_mode: true,
        cookie_flags: "SameSite=None;Secure",
        linker: {
          accept_incoming: true,
        },
      };

      if (typeof URLSearchParams !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);

        // Check for parent client ID (for cross-domain tracking in widgets)
        const parent_client_id = urlParams.get("parent_client_id");
        if (parent_client_id) {
          config = { ...config, client_id: parent_client_id };
        } else {
          // Fallback to regular client_id parameter
          const client_id = urlParams.get("client_id");
          if (client_id) {
            config = { ...config, client_id };
          }
        }

        // Check for parent session ID (for cross-domain tracking in widgets)
        const parent_session_id = urlParams.get("parent_session_id");
        if (parent_session_id) {
          config = { ...config, session_id: parent_session_id };
        } else {
          // Fallback to regular session_id parameter
          const session_id = urlParams.get("session_id");
          if (session_id) {
            config = { ...config, session_id };
          }
        }

        // Check for parent document referrer (for cross-domain tracking in widgets)
        const parent_document_referrer = urlParams.get(
          "parent_document_referrer"
        );
        if (parent_document_referrer) {
          config = { ...config, page_referrer: parent_document_referrer };
        }
      }

      window.gtag("config", tagId, { ...config });
    }
  };

  public disableGTag(tagId: string): void {
    if (!this.isGtagAvailable() || !this.activeTagIds.has(tagId)) {
      return;
    }

    // Disable the tag by setting its config to disable sending data
    if (window.gtag) {
      window.gtag("config", tagId, {
        send_page_view: false,
        send_to: tagId,
        restricted_data_processing: true,
      });
    }

    // eslint-disable-next-line
    // @ts-ignore
    window[`ga-disable-${tagId}`] = true; // disable the tag by setting the ga-disable-${tagId} to true

    this.activeTagIds.delete(tagId);
  }

  // New method to disable all currently active tags
  public disableAllTags(): void {
    if (!this.isGtagAvailable()) {
      return;
    }

    this.activeTagIds.forEach((tagId) => {
      this.disableGTag(tagId);
    });
  }

  public push = async (
    type: string,
    name: string,
    options: Record<string, unknown>
  ) => {
    const isGtagAvailable = this.isGtagAvailable();
    const hasActiveTagIds = this.activeTagIds.size > 0;

    if (isGtagAvailable && hasActiveTagIds) {
      // For each active tag ID, send the event with the specific tag as destination
      // if a tag is configured, but not active, the event will not be sent to that tag
      this.activeTagIds.forEach((tagId) => {
        window?.gtag?.(type, name, {
          ...options,
          send_to: tagId,
        });
      });
    } else {
      logAppInfo(`GTAG Available: ${isGtagAvailable}`);
      logAppInfo(`Has active tag ids: ${hasActiveTagIds}`);
      recordAppError(
        `Tried to push an analytics event for Google but script is unavailable OR there are no active tag ids.`
      );
    }
  };

  loadGTagScript = async (tagId: string) => {
    if (this.isGtagAvailable()) {
      return undefined;
    }

    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = new Promise<boolean>((resolve) => {
      // We only need to load the script once, for following Gtags, we can just use the config functionality
      const script = createElement("script") as HTMLScriptElement;

      script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
      script.async = true;
      script.onload = () => {
        window.dataLayer = window.dataLayer || [];

        // Define gtag function globally
        window.gtag = gtag;
        window.gtag("js", new Date());

        resolve(true);
      };
      appendScriptToHead(script);
    });

    return this.initializePromise;
  };
}

const gtagManager = new GTagManager();

export default gtagManager;
